<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "@/composables/useI18n";
import IconRatingStar from "@/svg/IconRatingStar.vue";

const { t } = useI18n();
const { maxRating = 5 } = defineProps<{
  maxRating?: number;
}>();
// Model we can set from the outside
const rating = defineModel<number>({ default: 0 });
// If we hover over a star, we set the rating to that value
const hoverRating = ref<number | null>(null);
// If we don't hover over a star, we show the rating to the value of the model otherwise we show the hovered rating
const displayRating = computed(() => hoverRating.value || rating.value);
const emit = defineEmits(["onRatingSet"]);

const resetRating = () => (hoverRating.value = null);

function setConversationRating(newrating: number) {
  hoverRating.value = newrating;
  rating.value = newrating;
  emit("onRatingSet", newrating);
}
</script>

<template>
  <div
    class="knw_widget_ratingbar"
    role="group"
    :aria-label="t('ratingbar.label') || 'Rate conversation'"
  >
    <span aria-hidden="true">{{ t("ratingbar.label") || "Rate conversation" }}</span>
    <div class="knw_widget_rating_stars" @mouseleave="resetRating">
      <button
        v-for="i in maxRating"
        :key="i"
        class="unstyled"
        :aria-label="t('ratingbar.buttonlabel', { count: i })"
        @mouseover="hoverRating = i"
        @focusin="hoverRating = i"
        @focusout="resetRating"
        @click="() => setConversationRating(i)"
      >
        <IconRatingStar
          width="16px"
          height="16px"
          :fill="displayRating >= i ? '#FF7555' : '#A9A9A9'"
        />
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.unstyled {
  background: none;
  border: none;
  padding: 0.75rem 0.0125rem;
  margin: 0;
  cursor: pointer;
}

.knw_widget_ratingbar {
  display: flex;
  gap: 1rem;
  flex-shrink: 1;
  min-height: 0;
  z-index: 1;
  width: 100%;
  justify-content: center;
  align-items: center;
  padding: 0;

  color: #222c50;
  font-size: 1.5rem;
}

.knw_widget_rating_stars {
  display: flex;
  cursor: pointer;
}
</style>
